import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';
import isEqual from 'react-fast-compare';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {ADD_ICON_WITH_HOVER_STYLES} from 'analysis-tool/AnalysisToolConstants';
import {useIsAnalysisLayoutCreationEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import {
  getAddDataFrameAnalysisViewItem,
  getEnabledDatasetCreationMethods,
  trackSelectedDataFrameCreationMethodEvent,
} from 'analysis-tool/data-frame-creation/DataFrameCreationUtils';
import {DEFAULT_LAYOUT_CONFIG} from 'analysis-tool/data-layout/utils/LayoutCreationUtils';
import {
  getMainViewItemForView,
  isLayoutView,
  isLegacyTopLevelAnalysisView,
  isTopLevelAnalysisView,
} from 'analysis-tool/data-view/AnalysisViewUtils';
import isAnalysisRunning from 'analysis-tool/isAnalysisRunning';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {recordAnalysisAction} from 'analysis-tool/ui/debugAnalysisActionHistory';
import useLogAnalysisProfilerTimingFinished from 'analysis-tool/ui/hooks/useLogAnalysisProfilerTimingFinished';
import {UseSelectedAnalysisItemOutput} from 'analysis-tool/ui/hooks/useSelectedAnalysisItem';
import {
  AnalysisMainViewItemType,
  isStepAwareAnalysisItem,
  isViewStepConfigAnalysisItem,
  StepInitializationAnalysisMainViewItem,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import ThirdPartyImportExportModal from 'analysis-tool/ui/main-view/third-party-step/ThirdPartyImportExportModal';
import {trackEvent} from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {ANALYSIS_TOOL_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ActionOrigin, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FILTER_TABLE_FINISHED_EVENT,
  RUN_ANALYSIS_TEMPLATE_FINISHED_EVENT,
} from 'performance/processAnalysesEvents';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getDefaultClientIdForView} from 'pipelines/PipelineViewUtils';
import {getLatestStepVersion} from 'pipelines/StepConnectionUtils';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import AddIcon from 'ui-library/icons/AddIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import type {JSONValue} from 'util/Types';

import AnalysisComponentErrorBoundary from '../components/AnalysisComponentErrorBoundary';
import {DEFAULT_HORIZONTAL_PADDING} from './AnalysisUISidebarConstants';
import CollapseExpandAllButton from './CollapseExpandAllButton';
import {getViewSidebarOverrides} from './getViewSidebarOverrides';
import {
  SidebarItemData,
  getOutputSidebarItemDatas,
  getAnalysisViewSidebarItemData,
  getLayoutSidebarItemDatas,
} from './SidebarItemDatas';
import SidebarItemWithActions from './SidebarItemWithActions';
import SidebarSearchBar from './SidebarSearchBar';
import SidebarSection from './SidebarSection';
import ToggleAnalysisSidebarButton from './ToggleAnalysisSidebarButton';
import useSidebarSearch, {SearchResultItem} from './useSidebarSearch';

const sidebarExpandedState = makeUseGlobalClientState<Record<string, boolean>>({});

export interface AnalysisUISidebarProps {
  analysis: PipelineGraphQL;
  onCloseSidebar: () => void;
  // Analysis steps that require configuration are special because they open intermediate views in the ui. We
  // should not have one onOpen action for each step type
  onOpenStepInitializationView: (newSelectedItem: StepInitializationAnalysisMainViewItem) => void;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  selectedItemState: UseSelectedAnalysisItemOutput;
}

/**
 * Wrapper component for handling shared archive-aware ui on sidebar `AddIcon` buttons.
 *
 * When analyses are archived, it renders a disabled `AddIcon` button with standard archived tooltip messaging;
 * otherwise, it renders the given button.
 */
function ArchivedButtonWrapper({
  analysis,
  children,
}: {
  analysis: PipelineGraphQL;
  children: ReactNode;
}): JSX.Element | null {
  const archivedAddIcon = (
    <Tooltip title={getArchivedTooltipMessage(analysis)}>
      <AddIcon disabled={true} />
    </Tooltip>
  );

  const archivedAndUnarchivable = isArchived(analysis) && analysis.permissions.unarchivable;

  return archivedAndUnarchivable ? archivedAddIcon : <>{children}</>;
}

/**
 * Renders a flat list of sidebar items for search results (no sections, no collapsible UI, no tiers).
 * Each item shows a breadcrumb subtitle indicating its position in the hierarchy.
 */
function SearchResultsList({
  analysis,
  items,
  selectedItemState,
  onOpenStepInitializationView,
  onChangeSelectedRightSidebarItem,
}: {
  analysis: PipelineGraphQL;
  items: Array<SearchResultItem>;
  selectedItemState: AnalysisUISidebarProps['selectedItemState'];
  onOpenStepInitializationView: AnalysisUISidebarProps['onOpenStepInitializationView'];
  onChangeSelectedRightSidebarItem: AnalysisUISidebarProps['onChangeSelectedRightSidebarItem'];
}): JSX.Element {
  return (
    <>
      {items.map(({item: itemData, breadcrumbSubtitle}, index) => {
        const {
          icon,
          title,
          mainViewItem,
          generateActionButtons,
          disabled,
          inactive,
          isFlaggingRows = false,
        } = itemData;
        const isSelected =
          isEqual(selectedItemState.selectedItem, mainViewItem) ||
          (isViewStepConfigAnalysisItem(selectedItemState.selectedItem) &&
            isStepAwareAnalysisItem(mainViewItem) &&
            selectedItemState.selectedItem.stepId === mainViewItem.stepId);
        const actionButtons =
          generateActionButtons != null
            ? generateActionButtons(
                analysis,
                onOpenStepInitializationView,
                onChangeSelectedRightSidebarItem,
                selectedItemState.onChangeSelectedItem
              )
            : undefined;
        return (
          <SidebarItemWithActions
            key={itemData.itemId ?? `search-${index}`}
            icon={icon}
            isSelected={isSelected}
            onSelect={() => {
              if (!disabled) {
                recordAnalysisAction('SIDEBAR_ITEM_SELECTED', {
                  previousItem: selectedItemState.selectedItem,
                  newItem: mainViewItem,
                  source: 'search',
                  index,
                });
                trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.SELECTED_SIDEBAR_ITEM, {
                  previousItem: selectedItemState.selectedItem,
                  newItem: mainViewItem,
                });
                selectedItemState.onChangeSelectedItem(mainViewItem);
              }
            }}
            title={title}
            disabled={disabled}
            inactive={inactive}
            subtitleText={breadcrumbSubtitle ?? undefined}
            isFlaggingRows={isFlaggingRows}
            dataTestAttrsKey={`search-${index}`}
            actionButtons={actionButtons}
          />
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sidebar content
// ---------------------------------------------------------------------------

function handleToggleExpandAllHelper(
  itemDatas: Array<SidebarItemData>,
  currentStates: Record<string, boolean>,
  isAnyItemExpanded: boolean
): void {
  itemDatas.forEach((itemData) => {
    if (itemData.itemId == null) {
      return;
    }
    currentStates[itemData.itemId] = !isAnyItemExpanded;
    // Recursively update children
    handleToggleExpandAllHelper(itemData.childSidebarItems ?? [], currentStates, isAnyItemExpanded);
  });
}

function SidebarContent({
  analysis,
  onCloseSidebar,
  onOpenStepInitializationView,
  onChangeSelectedRightSidebarItem,
  selectedItemState,
}: AnalysisUISidebarProps): JSX.Element {
  const [allExpandedStates, setAllExpandedStates] = sidebarExpandedState.useGlobalClientState(analysis.id);
  const dataFrameCreationMethods = getEnabledDatasetCreationMethods();

  const layoutCreationEnabled = useIsAnalysisLayoutCreationEnabled();
  const viewSidebarOverrides = getViewSidebarOverrides(analysis, {
    excludeLayoutChildViews: layoutCreationEnabled,
  });

  const nonLegacyViews = analysis.views.filter((view) => !isLegacyTopLevelAnalysisView(view, analysis.steps));
  const topLevelViews = nonLegacyViews.filter(
    (view) =>
      isTopLevelAnalysisView(view, analysis) &&
      !viewSidebarOverrides.skipViewIds.has(getDefaultClientIdForView(view, analysis.steps)) &&
      (!layoutCreationEnabled || !isLayoutView(view, analysis.steps))
  );

  const analysisViewIndexById = new Map(
    analysis.views.map((view, index) => [getDefaultClientIdForView(view, analysis.steps), index])
  );
  const topLevelViewItemDatas: Array<SidebarItemData> = [
    ...topLevelViews.map((view) => ({
      sortIndex:
        analysisViewIndexById.get(getDefaultClientIdForView(view, analysis.steps)) ?? Number.MAX_SAFE_INTEGER,
      sidebarItemData: getAnalysisViewSidebarItemData(view, analysis, viewSidebarOverrides),
    })),
    ...viewSidebarOverrides.topLevelGroupings.map((grouping) => {
      const firstChildView = grouping.childViews[0];
      return {
        sortIndex:
          analysisViewIndexById.get(getDefaultClientIdForView(firstChildView, analysis.steps)) ??
          Number.MAX_SAFE_INTEGER,
        sidebarItemData: {
          ...grouping.groupingItem,
          mainViewItem: getMainViewItemForView(firstChildView, analysis.steps),
          childSidebarItems: grouping.childViews.map((childView) =>
            getAnalysisViewSidebarItemData(childView, analysis, viewSidebarOverrides)
          ),
          itemId: `grouping-${getDefaultClientIdForView(firstChildView, analysis.steps)}`,
        } satisfies SidebarItemData,
      };
    }),
  ]
    .sort((left, right) => left.sortIndex - right.sortIndex)
    .map(({sidebarItemData}) => sidebarItemData);

  const outputSidebarItemDatas = getOutputSidebarItemDatas(analysis);
  const layoutSidebarItemDatas = layoutCreationEnabled
    ? getLayoutSidebarItemDatas(analysis, viewSidebarOverrides)
    : [];
  const canAddDataFrame = dataFrameCreationMethods.length > 0;

  // Build section arrays for search (all section item arrays)
  const allSections = [
    {sectionKey: 'Analysis data', items: topLevelViewItemDatas},
    ...(layoutCreationEnabled ? [{sectionKey: 'Layouts', items: layoutSidebarItemDatas}] : []),
    ...(outputSidebarItemDatas.length > 0 ? [{sectionKey: 'Outputs', items: outputSidebarItemDatas}] : []),
  ];

  const {query, setQuery, filteredItems} = useSidebarSearch(allSections.map((section) => section.items));

  const isAnyItemExpanded = allSections.some(
    ({sectionKey, items}) =>
      // if sectionKey doesn't exist in state, default to read state as True (consistent with visual render)
      allExpandedStates[sectionKey] !== false || resolveIsAnyExpanded(items)
  );

  function resolveIsAnyExpanded(itemDatas: Array<SidebarItemData>): boolean {
    return (
      itemDatas.some((data) => data.itemId != null && allExpandedStates[data.itemId] === true) ||
      itemDatas.some((data) => resolveIsAnyExpanded(data.childSidebarItems ?? []))
    );
  }

  function handleToggleExpand(itemId: string, isCurrentlyExpanded: boolean): void {
    setAllExpandedStates({...allExpandedStates, [itemId]: !isCurrentlyExpanded});
  }

  function handleToggleExpandAll(): void {
    const newStates = {...allExpandedStates};
    allSections.forEach(({sectionKey, items}) => {
      newStates[sectionKey] = !isAnyItemExpanded;
      handleToggleExpandAllHelper(items, newStates, isAnyItemExpanded);
    });
    setAllExpandedStates(newStates);
  }

  function handleAddDataset(): void {
    onOpenStepInitializationView(getAddDataFrameAnalysisViewItem());
    if (dataFrameCreationMethods.length === 1) {
      trackSelectedDataFrameCreationMethodEvent(
        ActionOrigin.ANALYSIS_TOOL_ANALYSIS_PAGE_DATA_FRAME_SIDEPANEL,
        analysis.id,
        dataFrameCreationMethods.length,
        dataFrameCreationMethods[0]
      );
    }
  }

  async function handleAddLayout(): Promise<void> {
    const response = await AnalysisActions.addAndUpdateSteps(analysis.id, {
      stepsToAdd: [
        {
          stepType: PipelineStepType.LAYOUT,
          stepVersion: getLatestStepVersion(PipelineStepType.LAYOUT),
          config: DEFAULT_LAYOUT_CONFIG as unknown as Record<string, JSONValue>,
          runAfterCreate: true,
        },
      ],
      stepsToUpdate: [],
    });
    const newStepId = response.newStepIds[0];
    if (newStepId != null) {
      selectedItemState.onChangeSelectedItem({
        type: AnalysisMainViewItemType.DATA_LAYOUT,
        stepId: newStepId,
      });
    }
  }

  useLogAnalysisProfilerTimingFinished(isAnalysisRunning(analysis), [
    RUN_ANALYSIS_TEMPLATE_FINISHED_EVENT,
    FILTER_TABLE_FINISHED_EVENT,
  ]);

  const isSearchActive = filteredItems != null;

  return (
    <div className={css(newSidebarStyles.sidebarContentWrapper)}>
      <SidebarSearchBar
        query={query}
        onQueryChange={setQuery}
        rightContent={
          <>
            <CollapseExpandAllButton
              isExpanded={isAnyItemExpanded}
              onToggleExpandAll={handleToggleExpandAll}
            />
            <ToggleAnalysisSidebarButton sidebarIsOpen={true} toggleSidebar={onCloseSidebar} />
          </>
        }
      />
      <div className={css(newSidebarStyles.sectionsWrapper)}>
        {isSearchActive ? (
          <SearchResultsList
            analysis={analysis}
            items={filteredItems}
            selectedItemState={selectedItemState}
            onOpenStepInitializationView={onOpenStepInitializationView}
            onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
          />
        ) : (
          <div className={css(newSidebarStyles.sectionsInner)}>
            <SidebarSection
              analysis={analysis}
              headerButton={
                <ArchivedButtonWrapper analysis={analysis}>
                  {analysis.permissions.writable ? (
                    <Tooltip disabled={!canAddDataFrame} title="Add data">
                      <AddIcon
                        altText="Add data"
                        className={css(newSidebarStyles.addIcon)}
                        disabled={!canAddDataFrame}
                        onClick={handleAddDataset}
                      />
                    </Tooltip>
                  ) : null}
                </ArchivedButtonWrapper>
              }
              sidebarItemDatas={topLevelViewItemDatas}
              selectedItemState={selectedItemState}
              onOpenStepInitializationView={onOpenStepInitializationView}
              title="Analysis data"
              onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
              expandedStates={allExpandedStates}
              onToggleExpand={handleToggleExpand}
            />
            {layoutCreationEnabled && (
              <div className={css(newSidebarStyles.secondarySidebarSectionWrapper)}>
                <SidebarSection
                  analysis={analysis}
                  headerButton={
                    <ArchivedButtonWrapper analysis={analysis}>
                      {analysis.permissions.writable ? (
                        <Tooltip title="Add layout">
                          <AddIcon
                            altText="Add layout"
                            className={css(newSidebarStyles.addIcon)}
                            onClick={handleAddLayout}
                          />
                        </Tooltip>
                      ) : null}
                    </ArchivedButtonWrapper>
                  }
                  sidebarItemDatas={layoutSidebarItemDatas}
                  selectedItemState={selectedItemState}
                  onOpenStepInitializationView={onOpenStepInitializationView}
                  title="Layouts"
                  onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
                  expandedStates={allExpandedStates}
                  onToggleExpand={handleToggleExpand}
                />
              </div>
            )}
            {outputSidebarItemDatas.length > 0 && (
              <div className={css(newSidebarStyles.secondarySidebarSectionWrapper)}>
                <SidebarSection
                  analysis={analysis}
                  defaultExpandedTier={0}
                  onOpenStepInitializationView={onOpenStepInitializationView}
                  sidebarItemDatas={outputSidebarItemDatas}
                  selectedItemState={selectedItemState}
                  title="Outputs"
                  onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
                  expandedStates={allExpandedStates}
                  onToggleExpand={handleToggleExpand}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className={css(newSidebarStyles.thirdPartyImportExportButtonWrapper)}>
        <Button
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          fullWidth={true}
          onClick={() => {
            legacyRunModal(ThirdPartyImportExportModal, {
              onRequestSubmit: () => {},
              onRequestClose: () => {},
              analysis,
            });
          }}
          size={ButtonSize.MEDIUM}
          text="Connect with external tool"
          dataTestAttrs={{component: 'AnalysisUISidebar', element: 'thirdPartyImportButton'}}
        />
      </div>
    </div>
  );
}

const newSidebarStyles = StyleSheet.create({
  sidebarContentWrapper: {
    // Pull the content mostly to the left edge of the sidebar, leaving 4px of breathing
    // room. The parent sidebarWrapper has 12px left padding; we negate 8px of it.
    marginLeft: -(DEFAULT_HORIZONTAL_PADDING - 4),
    paddingRight: 4,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    // Prevent the parent sidebarWrapper's overflow:auto from scrolling the search bar.
    // Instead, only sectionsWrapper scrolls.
    overflow: 'hidden',
  },
  sectionsWrapper: {
    flexGrow: 1,
    overflowX: 'auto',
    overflowY: 'auto',
    // Prevent elastic bounce when overscrolling horizontally (macOS/iOS)
    overscrollBehaviorX: 'none',
  },
  sectionsInner: {
    // Makes this container as wide as the widest item, but at least as wide as the
    // visible scroll area. All block children then stretch to this width, ensuring
    // short-named items are the same width as the longest item. This lets
    // position: sticky on the action buttons work correctly for all items.
    width: 'max-content',
    minWidth: '100%',
  },
  secondarySidebarSectionWrapper: {
    marginTop: 16,
  },
  thirdPartyImportExportButtonWrapper: {
    marginTop: 8,
    paddingLeft: 8,
    paddingRight: 4,
  },
  addIcon: ADD_ICON_WITH_HOVER_STYLES,
});

// ---------------------------------------------------------------------------
// Top-level sidebar component with error boundary
// ---------------------------------------------------------------------------

export default function AnalysisUISidebar(props: AnalysisUISidebarProps): JSX.Element {
  return (
    <AnalysisComponentErrorBoundary
      errorExtras={{analysisId: props.analysis.id, selectedItemState: props.selectedItemState}}
    >
      <SidebarContent {...props} />
    </AnalysisComponentErrorBoundary>
  );
}
